/**
 * Workflow intake — the view-model that makes the existing compile path
 * renderable.
 *
 * ── Why this module exists ──
 *
 * The six-persona buyer gauntlet (2026-08-17) found that the product's central
 * capability is built and invisible: `verifier3/spec-compiler.ts` compiles a
 * natural-language workflow document into kernel-checked mechanical predicates
 * via an in-boundary local model, and `dod-derive.ts` mints independent oracles
 * from a catalog — but no product surface exposes either, so the documented
 * onboarding hands the customer homework the engine could already do. The
 * gauntlet's blocker F3 is "expose the derivation the engine already has."
 *
 * This module is that exposure, and deliberately nothing more. It does not
 * compile, does not call a model, and does not decide admission on its own
 * authority. It takes what the compiler produced and turns it into rows a page
 * can render, applying the product's refusals at the view boundary as a second
 * line of defence rather than a first.
 *
 * ── Why the refusals are re-applied here ──
 *
 * A view-model that trusts its input is where an invariant quietly dies. The
 * compiler enforces grounding with a requirement-family regex
 * (`draftGroundedInDoc`); this module additionally requires the predicate's
 * `source_span` to be a literal substring of the submitted document. Those two
 * checks fail differently on purpose: the compiler catches a model that invents
 * a requirement family the doc never names, and this catches a model that
 * fabricates a quotation. Neither subsumes the other, and a predicate the
 * customer sees attributed to "their" document had better actually be in it.
 *
 * ── The one thing this module may never do ──
 *
 * There is no field anywhere in `WorkflowIntakeView` that can attribute work to
 * a remote model. G5 makes frontier models structurally unreachable in the
 * compile path; representing one here would be the beginning of undoing that,
 * because a view-model is exactly where "just for display" starts. The
 * `model` field carries the local model id the compiler reported and nothing
 * else, and `test-verification-workflow-intake` asserts the absence.
 */
export const INTAKE_PREDICATE_KINDS = [
    "checkpoint",
    "band",
    "external-oracle",
    "ordering",
    "row",
];
/**
 * The catalog the intake surface offers.
 *
 * The three `catalog` entries mirror `INDEPENDENT_ORACLE_SPECS` in
 * `packages/excellent-mcp/src/dod-derive.ts`. They are restated here rather
 * than imported because `@excellent/core` may not depend on the MCP package
 * (the dependency runs the other way — the MCP vendors core). The gate asserts
 * the keys agree with that source, so the restatement cannot drift.
 *
 * The `external` entries are the vendor adapters. Every one is `bindable:
 * false` today, and that is not an oversight: `verifier3/oracle-adapter.ts`
 * forbids live network calls, and live adapters are deliberately
 * specimen-earned — they ship only after a real workflow's claim could not be
 * discharged without one. Offering them as bindable would be the product lying
 * about its own boundary on the very screen that exists to show the boundary.
 */
export const INTAKE_ORACLE_CATALOG = [
    {
        key: "git-tree",
        kind: "git-tree",
        origin: "catalog",
        proves: "the tree under review is a real git tree object, not a claimed one",
        forge: "a git tree object whose hash matches the attributable commits — an agent can write any row, it cannot make the object store agree",
        bindable: true,
    },
    {
        key: "gate-receipt-binding",
        kind: "gate-receipt-binding",
        origin: "catalog",
        proves: "a gate receipt binds this tree hash to the work under review with binding=staged",
        forge: "a staged receipt whose tree_hash equals the real git tree of the shipped work — writing a row is not enough",
        bindable: true,
    },
    {
        key: "two-agent-identity",
        kind: "two-agent-identity",
        origin: "catalog",
        proves: "the actor who did the work and the actor who accepted it are different identities",
        forge: "a second process-tree-derived agent id — identity is not pickable and cannot be spoofed from the payload",
        bindable: true,
    },
    {
        key: "stripe-refund",
        kind: "external-oracle",
        origin: "external",
        proves: "the refund the workflow claims to have issued exists in the payment processor's own record",
        forge: "a Stripe-side refund object — the processor's ledger is not writable from the agent's side",
        bindable: false,
        unbindableReason: "Live vendor adapters are specimen-earned: the adapter ships only after a real workflow's claim could not be discharged without it. Recorded-fixture replay is available today.",
    },
    {
        key: "okta-grant",
        kind: "external-oracle",
        origin: "external",
        proves: "the access grant exists in the identity provider, and its absence is checkable",
        forge: "an Okta-side assignment record — absence is checkable in a way a 'revoked successfully' log line is not",
        bindable: false,
        unbindableReason: "Live vendor adapters are specimen-earned: the adapter ships only after a real workflow's claim could not be discharged without it. Recorded-fixture replay is available today.",
    },
    {
        key: "jira-approval",
        kind: "external-oracle",
        origin: "external",
        proves: "the approval the workflow cites was recorded by a system the actor does not control",
        forge: "a Jira transition authored by a different account than the one under verification",
        bindable: false,
        unbindableReason: "Live vendor adapters are specimen-earned: the adapter ships only after a real workflow's claim could not be discharged without it. Recorded-fixture replay is available today.",
    },
];
/** The catalog keys that mirror `INDEPENDENT_ORACLE_SPECS`. Asserted by the gate. */
export const INTAKE_CATALOG_ORACLE_KEYS = INTAKE_ORACLE_CATALOG.filter((o) => o.origin === "catalog").map((o) => o.key);
/* ── Fail-closed messaging ───────────────────────────────────────────────── */
const FAIL_CLOSED_COPY = {
    "local-model-absent": {
        message: "Compilation needs the in-boundary local model, and it is not running. Nothing was derived — the engine refuses to guess, and it will not fall back to a hosted model.",
        fixCommand: "ollama pull qwen2.5-coder:7b",
    },
    "frontier-forbidden": {
        message: "A hosted frontier model was offered to the compile path and refused. The compile boundary is local-only by design; this is the refusal working, not a misconfiguration.",
        fixCommand: null,
    },
    "empty-doc": {
        message: "The submitted document was empty, so there was nothing to derive predicates from.",
        fixCommand: null,
    },
    "local-model-unparseable": {
        message: "The local model returned output the kernel could not parse into predicates. Nothing was admitted; retrying is safe, and repeated failure means the document needs to state its checkpoints more explicitly.",
        fixCommand: null,
    },
};
/**
 * The operator-facing copy for a fail-closed compile — message and, when the
 * block is operator-fixable, the one command that fixes it.
 *
 * ── Why this is EXPORTED (#46363) ──
 *
 * This copy existed and nothing outside this module could reach it. The
 * documented "Fast Operator Path" failed at step 3 on an absent local model and
 * the operator saw:
 *
 *   {"ok":true,"data":{"ok":false,"reason":"compile failed closed
 *     (local-model-absent: spec-compiler: local model absent (ollama runtime
 *     not reachable))"}}
 *
 * — while THIS TABLE already held the sentence that tells them what happened
 * and the command that fixes it. The product knew the answer; the surface the
 * user was standing on could not ask for it. A field written and never read.
 *
 * Exported so every fail-closed surface renders the same words. A second copy
 * of this text somewhere else is the defect coming back.
 */
export function failClosedCopy(reason) {
    return FAIL_CLOSED_COPY[reason];
}
/**
 * Whether `value` is a reason this module has copy for.
 *
 * The compiler's `FailClosedReason` and `IntakeFailClosedReason` are two
 * declarations of one vocabulary, and they can fork: a new reason added to the
 * compiler without copy here would reach an operator as a blank message. This
 * predicate is how a caller that receives a reason as DATA — across a route, a
 * catch block, a JSON envelope — checks before rendering, instead of indexing a
 * record with a string and getting `undefined`.
 */
export function isIntakeFailClosedReason(value) {
    return typeof value === "string" && Object.prototype.hasOwnProperty.call(FAIL_CLOSED_COPY, value);
}
/** Every reason this module can render. Exported so a gate can assert parity. */
export const INTAKE_FAIL_CLOSED_REASONS = Object.keys(FAIL_CLOSED_COPY);
/* ── Oracle sufficiency ──────────────────────────────────────────────────── */
const NON_ORACLE_TOKENS = /^(none|n\/a|na|nil|null|unknown|tbd|todo|no oracle|not sure|pending|later)$/;
/**
 * Whether a bound oracle string counts as naming an independent oracle.
 *
 * Deliberately identical in behaviour to `hasIndependentOracle` in
 * `excellent/server/evidence-workflow-intake.ts`, which is the live server-side
 * admission path. Two copies of an admission rule that disagree is worse than
 * either copy, so the gate asserts both accept and reject the same strings.
 */
export function intakeOracleIsIndependent(value) {
    if (typeof value !== "string")
        return false;
    const normalized = value.trim().toLowerCase();
    if (normalized.length < 4)
        return false;
    return !NON_ORACLE_TOKENS.test(normalized);
}
/* ── Grounding ───────────────────────────────────────────────────────────── */
/**
 * Locate a claimed source span in the submitted document.
 *
 * Whitespace is normalized on both sides before matching, because a model that
 * re-wraps a quoted line has not invented it — but the *content* must be
 * present verbatim. Returns offsets into the original document so the surface
 * can highlight the real text, not the normalized copy.
 */
export function locateSourceSpan(doc, span) {
    const needle = String(span ?? "").trim();
    if (!needle)
        return null;
    const direct = doc.indexOf(needle);
    if (direct >= 0)
        return { start: direct, end: direct + needle.length };
    // Whitespace-insensitive fallback: walk the doc, comparing collapsed runs.
    const collapse = (s) => s.replace(/\s+/g, " ");
    const flatNeedle = collapse(needle);
    if (!flatNeedle)
        return null;
    // Build a map from collapsed-index -> original-index so a match in the
    // collapsed doc can be reported as an offset into the real one.
    let flatDoc = "";
    const originalIndex = [];
    let inRun = false;
    for (let i = 0; i < doc.length; i += 1) {
        const ch = doc[i] ?? "";
        if (/\s/.test(ch)) {
            if (inRun)
                continue;
            inRun = true;
            flatDoc += " ";
            originalIndex.push(i);
            continue;
        }
        inRun = false;
        flatDoc += ch;
        originalIndex.push(i);
    }
    const hit = flatDoc.indexOf(flatNeedle);
    if (hit < 0)
        return null;
    const start = originalIndex[hit];
    const lastFlat = hit + flatNeedle.length - 1;
    const endAnchor = originalIndex[lastFlat];
    if (start === undefined || endAnchor === undefined)
        return null;
    return { start, end: endAnchor + 1 };
}
/* ── The builder ─────────────────────────────────────────────────────────── */
/**
 * Turn a compile outcome into the renderable intake view.
 *
 * Pure: no clock, no I/O, no model. Everything it decides, it decides from its
 * argument, which is what makes the gate able to drive every branch including
 * the ones a live compiler would rarely produce.
 */
export function buildWorkflowIntakeView(input) {
    const doc = typeof input.doc === "string" ? input.doc : "";
    const name = (input.name ?? "").trim() || "Untitled workflow";
    const hasOracle = intakeOracleIsIndependent(input.boundOracle);
    // ── Fail-closed: nothing was derived, and the view must not imply otherwise.
    if (input.outcome.state === "fail-closed") {
        // Through the exported accessor, not the table, so this view and every
        // other fail-closed surface are reading the SAME words by construction.
        const copy = failClosedCopy(input.outcome.reason);
        return {
            name,
            docLength: doc.length,
            predicates: [],
            rejectedDrafts: [],
            admission: {
                status: "reject",
                hasIndependentOracle: hasOracle,
                // The compile refusal is the operative reason even when an oracle is
                // bound: an oracle without predicates has nothing to be independent OF.
                reasons: [`compile-fail-closed:${input.outcome.reason}`],
            },
            oracleCandidates: [],
            blocked: {
                reason: input.outcome.reason,
                message: copy.message,
                fixCommand: copy.fixCommand,
                ...(input.outcome.detail === undefined ? {} : { detail: input.outcome.detail }),
            },
            model: null,
            attempts: 0,
            admittedSet: null,
        };
    }
    const kinds = new Set(INTAKE_PREDICATE_KINDS);
    const predicates = [];
    const rejectedDrafts = [];
    // Carry the compiler's own rejections through first, so the order the
    // customer reads matches the order the engine decided.
    for (const r of input.outcome.rejected) {
        rejectedDrafts.push({
            draft: r.draft,
            reason: "compiler-rejected",
            detail: r.reason,
        });
    }
    for (const p of input.outcome.predicates) {
        if (!kinds.has(p.kind)) {
            rejectedDrafts.push({
                draft: p.expr,
                reason: "unknown-kind",
                detail: `kind '${String(p.kind)}' is not in the kernel vocabulary`,
            });
            continue;
        }
        const offset = locateSourceSpan(doc, p.source_span);
        if (offset === null) {
            // The model attributed a phrase to the customer's document that is not in
            // it. Surfaced rather than dropped: it is evidence about the model.
            rejectedDrafts.push({
                draft: p.expr,
                reason: "ungrounded-in-source",
                inventedSpan: p.source_span,
            });
            continue;
        }
        predicates.push({
            id: p.id,
            kind: p.kind,
            tier: p.tier,
            expr: p.expr,
            falsifier: p.falsifier,
            sourceSpan: p.source_span,
            sourceOffset: offset,
        });
    }
    const admission = decideAdmission({ predicates, hasOracle });
    return {
        name,
        docLength: doc.length,
        predicates,
        rejectedDrafts,
        admission,
        oracleCandidates: admission.status === "needs-oracle" ? [...INTAKE_ORACLE_CATALOG] : [],
        blocked: null,
        model: input.outcome.model,
        attempts: input.outcome.attempts,
        admittedSet: admission.status === "admit" ? (input.admittedSet ?? null) : null,
    };
}
/**
 * The admission decision, isolated so the gate can drive it directly.
 *
 * Rule 1 lives here: no independent oracle → no admission. The ordering matters
 * — a workflow with no predicates is `reject` even with an oracle bound,
 * because there is nothing for the oracle to be independent of, and a
 * `needs-oracle` verdict would invite the customer to bind an oracle that
 * could not help.
 */
export function decideAdmission(input) {
    const reasons = [];
    if (input.predicates.length === 0) {
        reasons.push("no-grounded-predicates");
        return { status: "reject", hasIndependentOracle: input.hasOracle, reasons };
    }
    if (!input.hasOracle) {
        reasons.push("no-independent-oracle");
        return { status: "needs-oracle", hasIndependentOracle: false, reasons };
    }
    reasons.push("grounded-predicates-present", "independent-oracle-bound");
    return { status: "admit", hasIndependentOracle: true, reasons };
}
/**
 * Bind an oracle candidate to a pending workflow, returning the re-decided
 * admission. The `needs-oracle` conversation's terminal move.
 *
 * Refuses a non-bindable candidate rather than admitting on a promise: the
 * specimen-earned policy is the reason the external adapters are listed at all,
 * and a bind that ignored `bindable` would turn the honest catalog into a
 * decorative one.
 */
export function bindOracleCandidate(input) {
    const candidate = INTAKE_ORACLE_CATALOG.find((o) => o.key === input.candidateKey);
    if (!candidate) {
        return {
            ok: false,
            reason: "unknown-oracle-candidate",
            detail: `'${input.candidateKey}' is not in the oracle catalog`,
        };
    }
    if (!candidate.bindable) {
        return {
            ok: false,
            reason: "oracle-not-bindable",
            detail: candidate.unbindableReason ?? "This oracle cannot be bound yet.",
        };
    }
    if (input.view.blocked !== null) {
        return {
            ok: false,
            reason: "compile-fail-closed",
            detail: "Nothing was derived from the document, so an oracle has nothing to bind to.",
        };
    }
    const admission = decideAdmission({ predicates: input.view.predicates, hasOracle: true });
    return {
        ok: true,
        view: { ...input.view, admission, oracleCandidates: [], admittedSet: null },
    };
}
